import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Button from "../../common/Button";
import { useScrambleText } from "../../../hooks/useScrambleText";

interface EventsShowcaseProps {
  id?: string;
  title?: string;
  subheading?: string;
  description?: string;
  date?: string;
  time?: string;
  location?: string;
  mode?: string;
  entryFee?: string;
  prizePool?: string;
  awards?: string;
  sponsor?: string;
  backgroundImage?: string;
  characterImage?: string;
  reverse?: boolean;
  isAboveFold?: boolean; // True for first showcase to optimize LCP
}

// Optimized Framer Motion Variants for cleaner component tree and Staggered Children
const staggeredContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.35,
    },
  },
};

const fadeUpVariant: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const chipVariant: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

const EventsShowcase: React.FC<EventsShowcaseProps> = ({
  id,
  title,
  subheading,
  description,
  date,
  time,
  location,
  mode,
  entryFee,
  prizePool,
  sponsor,
  backgroundImage,
  characterImage,
  reverse = false,
  isAboveFold = false,
}) => {
  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.15, // Trigger slightly earlier for a seamless scroll experience
  });
  const sectionRef = useRef<HTMLElement | null>(null);

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" ? window.innerWidth < 800 : false);
  const [isPowered, setIsPowered] = useState(false);
  const { displayedText: scrambledTitle } = useScrambleText({
    text: title || "",
    enabled: inView,
    speed: 60,
  });

  // Debounced Resize Listener to prevent layout thrashing
  useEffect(() => {
    let debounceTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setIsMobile(window.innerWidth < 800);
      }, 150);
    };

    window.addEventListener("resize", handleResize, { passive: true });
    
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const setRefs = (node: HTMLElement | null) => {
    inViewRef(node);
    sectionRef.current = node;
  };

  return (
    <section
      id={id}
      ref={setRefs}
      className={`position-relative overflow-hidden rounded-4 mb-5 karmatek-showcase ${
        isMobile ? "d-flex flex-column" : ""
      } ${isPowered ? "is-powered" : ""}`}
      style={{
        minHeight: isMobile ? "auto" : "750px",
        height: "auto",
      }}
    >
      <div className="karmatek-showcase-rail" aria-hidden="true" />
      
      {/* Background Image - Layer 1: Cinematic Scene Setter */}
      {backgroundImage && (
        <motion.img
          initial={{ opacity: 0, scale: 1.05 }}
          animate={inView ? { opacity: 0.55, scale: 1 } : {}}
          transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="position-absolute w-100"
          src={backgroundImage}
          alt=""
          aria-hidden="true"
          draggable="false"
          loading={isAboveFold ? "eager" : "lazy"}
          fetchPriority={isAboveFold ? "high" : undefined}
          decoding="async"
          width="1920"
          height="1080"
          style={{
            bottom: 0,
            height: "70%",
            objectFit: "cover",
            zIndex: 1,
            pointerEvents: "none",
            aspectRatio: "16 / 9",
          }}
        />
      )}

      {/* Character Image - Layer 3: Hero Power Moment */}
      {/* Consolidated DOM nodes for mobile and desktop to prevent React remounting on resize */}
      <motion.div
        initial={{ opacity: 0, x: isMobile ? 0 : (reverse ? -60 : 60), scale: 0.9 }}
        animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
        transition={{
          duration: 0.6,
          delay: 0.25,
          ease: [0.22, 1, 0.36, 1],
        }}
        className={
          isMobile
            ? "w-100 d-flex flex-column align-items-center justify-content-end pt-3 px-2"
            : "position-absolute top-0 h-100"
        }
        style={{
          zIndex: 3,
          ...(isMobile ? {} : {
            width: "40%",
            [reverse ? "left" : "right"]: 0,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: reverse ? "flex-start" : "flex-end",
            overflow: "hidden",
          })
        }}
      >
        <img
          src={characterImage}
          alt="character"
          draggable="false"
          loading={isAboveFold ? "eager" : "lazy"}
          fetchPriority={isAboveFold ? "high" : undefined}
          decoding="async"
          width="600"
          height="800"
          style={{
            height: isMobile ? "clamp(200px, 40vh, 360px)" : "100%",
            width: isMobile ? "auto" : "100%",
            objectFit: "contain",
            pointerEvents: "none",
            aspectRatio: "3 / 4",
            willChange: "transform",
          }}
        />
      </motion.div>

      {/* Text Details Box - Layer 2: Information Reveal */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{
          duration: 0.6,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1],
        }}
        className={`w-100 ${
          isMobile
            ? "d-flex justify-content-center align-items-start p-3 p-md-4"
            : "position-absolute top-0 start-0 h-100 d-flex align-items-center"
        }`}
        style={{
          zIndex: 4,
          willChange: inView ? "auto" : "transform, opacity",
          ...(isMobile
            ? {}
            : {
                padding: "2rem",
                justifyContent: reverse ? "flex-end" : "flex-start",
                paddingRight: reverse ? "10%" : "2rem",
                paddingLeft: reverse ? "2rem" : "10%",
              }),
        }}
      >
        <div
          className={`text-white karmatek-power-card ${
            isPowered ? "is-powered" : ""
          }`}
          style={{
            maxWidth: isMobile ? "100%" : "50%",
            minWidth: "280px",
            // Upgraded Premium Glassmorphism Design
            background: "linear-gradient(135deg, rgba(15, 23, 42, 0.75), rgba(15, 23, 42, 0.45))",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "24px",
            boxShadow: "0 16px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
            padding: isMobile ? "1.5rem" : "2.5rem",
          }}
          onClick={() => setIsPowered((prev) => !prev)}
          role="button"
          aria-pressed={isPowered}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setIsPowered((prev) => !prev);
            }
          }}
        >
          {/* Title with Scramble Reveal */}
          <motion.h2
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.3, delay: 0.25 }}
            className="fw-bold mb-3"
            style={{
              fontSize: "clamp(1.75rem, 5vw, 2.75rem)",
              textShadow: "0 2px 10px rgba(0,0,0,0.5)",
              letterSpacing: "-0.02em"
            }}
          >
            {scrambledTitle}
          </motion.h2>

          {/* Staggered Content Container */}
          <motion.div
            variants={staggeredContainer}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="d-flex flex-column"
          >
            {/* Subtitle */}
            {subheading && (
              <motion.div
                variants={fadeUpVariant}
                className="text-info text-uppercase fw-semibold mb-3"
                style={{ letterSpacing: "0.1em", fontSize: "0.9rem" }}
              >
                {subheading}
              </motion.div>
            )}

            {/* Description */}
            {description && (
              <motion.p
                variants={fadeUpVariant}
                className="mb-4"
                style={{
                  fontSize: "clamp(1rem, 2.5vw, 1.15rem)",
                  lineHeight: "1.7",
                  color: "rgba(255,255,255,0.85)", // Softer text color for premium feel
                }}
              >
                {description}
              </motion.p>
            )}

            <div className="d-flex flex-column gap-3">
              {/* First Row Chips */}
              <div className="d-flex flex-wrap align-items-center gap-2">
                {date && (
                  <motion.span
                    variants={chipVariant}
                    className="badge text-dark fw-bold px-3 py-2 rounded-pill"
                    style={{
                      fontSize: "clamp(0.85rem, 2.5vw, 0.95rem)",
                      background: "linear-gradient(135deg,#22d3ee,#0ea5b9)",
                      boxShadow: "0 4px 12px rgba(34, 211, 238, 0.25)"
                    }}
                  >
                    {date}
                  </motion.span>
                )}

                {time && (
                  <motion.span
                    variants={chipVariant}
                    className="badge text-light px-3 py-2 rounded-pill"
                    style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.05)" }}
                  >
                    {time}
                  </motion.span>
                )}

                {(location || mode) && (
                  <motion.span
                    variants={chipVariant}
                    className="badge text-light px-3 py-2 rounded-pill"
                    style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.05)" }}
                  >
                    {mode ? `${mode}` : ""}
                    {mode && location ? " • " : ""}
                    {location || ""}
                  </motion.span>
                )}
              </div>

              {/* Second Row Chips */}
              <div className="d-flex flex-wrap align-items-center gap-2">
                {entryFee && (
                  <motion.span
                    variants={chipVariant}
                    className="badge text-light px-3 py-2 rounded-pill"
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    Entry: {entryFee}
                  </motion.span>
                )}

                {prizePool && (
                  <motion.span
                    variants={chipVariant}
                    className="badge text-light px-3 py-2 rounded-pill"
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    Prize Pool: {prizePool}
                  </motion.span>
                )}

                {sponsor && (
                  <motion.span
                    variants={chipVariant}
                    className="d-flex align-items-center gap-2"
                  >
                    <small className="text-secondary" style={{ fontSize: "0.8rem" }}>Sponsored by</small>
                    <span
                      className="badge text-white px-3 py-1.5 rounded-pill"
                      style={{
                        background: "linear-gradient(135deg,#0ea5b9,#0369a1)",
                        fontWeight: 700,
                        border: "1px solid rgba(14, 165, 185, 0.3)"
                      }}
                    >
                      {sponsor}
                    </span>
                  </motion.span>
                )}

                {id && (
                  <motion.div variants={chipVariant} className="ms-1 mt-2 mt-sm-0">
                    <Link
                      to={`/karmatek/event/${id}`}
                      className="text-decoration-none"
                    >
                      <Button
                        text="View Details"
                        gradient="linear-gradient(90deg,#05a5ff,#00d4ff)"
                      />
                    </Link>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default EventsShowcase;
